/**
 * Automations — content bundle for the WAC UBP calculator.
 *
 * Source of truth: Confluence "Automation: Content spec for WAC Calculator"
 * https://hello.atlassian.net/wiki/spaces/acbp/pages/7129271516
 *
 * Content DRI: Belle Craig · Meter CD: Lauren Ryan
 *
 * Fields we haven't finalized are simply OMITTED from this file (most
 * are optional at the type level) so nothing renders for them. Do NOT
 * stub placeholder strings — omit the field entirely instead. There is
 * no `PLACEHOLDER` / `todoCopy` helper exported from this module by
 * design.
 *
 * DO NOT edit unrelated meter files when updating this one — the atomic
 * per-meter file split exists to prevent merge conflicts between authors.
 *
 * i18n: every copy field is wrapped in `defineMessage` with a literal
 * `id` and `defaultMessage`. The extractor is a static AST analyzer —
 * see the i18n contract in `meter-content.types.ts`. Keep both fields
 * literal; never build an id from a variable.
 */

import { defineMessage } from 'react-intl';

import { wacToken } from '@/libraries/design/foundations';

import type { MeterContent } from './meter-content.types';

export const AUTOMATIONS_CONTENT: MeterContent = {
	description: defineMessage({
		id: 'ubp.calculator.usage.automations.description',
		defaultMessage:
			'Give your team valuable time back by reducing repetitive manual work with automation flows.',
	}),
	needsStep: {
		// Step 2 "Do you know your usage?" question — Automations copy.
		doYouKnowQuestion: defineMessage({
			id: 'ubp.calculator.needs.automations.doYouKnowQuestion',
			defaultMessage: 'Know how many Automation steps you’ll need?',
		}),
	},
	estimateStep: {
		subtitle: defineMessage({
			id: 'ubp.calculator.estimate.automations.subtitle',
			defaultMessage:
				'Based on what you’ve told us, here’s a summary of how many Automation steps you need, and how you can use them.',
		}),
		compareCta: defineMessage({
			id: 'ubp.calculator.estimate.automations.compareCta',
			defaultMessage: 'Explore plans',
		}),
		// TEMPORARY: All meters currently share this Orange Logic asset
		// while the content team finalizes per-meter hero imagery.
		image: {
			src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/bb1q20l5xjp2i85j55ou317euquo3rv3.png',
			alt: '',
			// eslint-disable-next-line local/ensure-wac-token-usage
			imageBackgroundColor: wacToken('color.extended.blue100'),
		},
		// ContentBlock copy per revised design direction (2026-07-16):
		// reframe Card 1 around ROI + built-in allowances with the
		// "Explore plans" CTA. Shared across all non-CSR meters.
		contentBlock: {
			title: defineMessage({
				id: 'ubp.calculator.estimate.automations.contentBlock.title',
				defaultMessage: 'Maximize your ROI with flexible, usage-based plans',
			}),
			intro: defineMessage({
				id: 'ubp.calculator.estimate.automations.contentBlock.intro',
				defaultMessage:
					'With Atlassian collections and apps, built-in allowances align your costs with your real-world usage.',
			}),
			listItems: [
				defineMessage({
					id: 'ubp.calculator.estimate.automations.contentBlock.listItems.0',
					defaultMessage: 'Optimize your spend',
				}),
				defineMessage({
					id: 'ubp.calculator.estimate.automations.contentBlock.listItems.1',
					defaultMessage: 'Plan with confidence',
				}),
				defineMessage({
					id: 'ubp.calculator.estimate.automations.contentBlock.listItems.2',
					defaultMessage: 'Do more with less',
				}),
			],
			href: '/collections',
		},
	},
	allowanceStep: {
		// Step 4 primary CTA — per Automation Steps Confluence spec.
		// Overrides the generic "Next" with a comparison-focused verb.
		nextCta: defineMessage({
			id: 'ubp.calculator.allowance.automations.nextCta',
			defaultMessage: 'Compare',
		}),
	},
	summaryStep: {
		// Step 5 header — per Automations Confluence spec.
		title: defineMessage({
			id: 'ubp.calculator.summary.automations.title',
			defaultMessage: 'Review usage estimate and compare',
		}),
		subtitle: defineMessage({
			id: 'ubp.calculator.summary.automations.subtitle',
			defaultMessage:
				'Based on what you’ve told us, here’s a summary of how many Automation steps you need, and how you can use them.',
		}),
		orgNeedsCard: {
			eyebrow: defineMessage({
				id: 'ubp.calculator.summary.automations.orgNeedsCard.eyebrow',
				defaultMessage: 'YOUR ORGANIZATION NEEDS',
			}),
			headlineSubtext: defineMessage({
				id: 'ubp.calculator.summary.automations.orgNeedsCard.headlineSubtext',
				defaultMessage: 'Automation steps per month',
			}),
		},
		whatsCoveredCard: {
			eyebrow: defineMessage({
				id: 'ubp.calculator.summary.automations.whatsCoveredCard.eyebrow',
				defaultMessage: 'WHAT’S COVERED',
			}),
			headlineSubtext: defineMessage({
				id: 'ubp.calculator.summary.automations.whatsCoveredCard.headlineSubtext',
				defaultMessage: 'Automation steps included monthly',
			}),
			bodyCovered: defineMessage({
				id: 'ubp.calculator.summary.automations.whatsCoveredCard.bodyCovered',
				defaultMessage: 'Your Automation steps are covered within the allowance.',
			}),
			bodyOverage: defineMessage({
				id: 'ubp.calculator.summary.automations.whatsCoveredCard.bodyOverage',
				defaultMessage:
					'There’s a gap of {overage} Automation steps. You may incur extra usage charges of {cost}.',
			}),
			listItemsCovered: [
				defineMessage({
					id: 'ubp.calculator.summary.automations.whatsCoveredCard.listItemsCovered.0',
					defaultMessage: 'Higher plans provide more allowances',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.automations.whatsCoveredCard.listItemsCovered.1',
					defaultMessage:
						'Track your extra usage via your capabilities subscription details page in admin hub',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.automations.whatsCoveredCard.listItemsCovered.2',
					defaultMessage: 'Set an extra usage limit to stay in budget',
				}),
			],
			listItemsOverage: [
				defineMessage({
					id: 'ubp.calculator.summary.automations.whatsCoveredCard.listItemsOverage.0',
					defaultMessage: 'Higher plans provide more allowances',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.automations.whatsCoveredCard.listItemsOverage.1',
					defaultMessage:
						'Track your extra usage via your capabilities subscription details page in admin hub',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.automations.whatsCoveredCard.listItemsOverage.2',
					defaultMessage: 'Set an extra usage limit to stay in budget',
				}),
			],
		},
		// Automations spec: share button label is "Share with team" instead
		// of the default "Copy URL to share".
		shareCta: defineMessage({
			id: 'ubp.calculator.summary.automations.shareCta',
			defaultMessage: 'Share with team',
		}),
	},
	usageProfiles: {
		exploring: {
			label: defineMessage({
				id: 'ubp.calculator.needs.automations.exploring.label',
				defaultMessage: 'Experimenting',
			}),
			description: defineMessage({
				id: 'ubp.calculator.needs.automations.exploring.description',
				defaultMessage:
					'Best for automating simple tasks for one app such as Jira, or a single space or team.',
			}),
			// usageRange omitted — Automations bullets already convey the range.
			list: [
				defineMessage({
					id: 'ubp.calculator.needs.automations.exploring.list.0',
					defaultMessage: '1–10 steps run per seat per month',
				}),
				defineMessage({
					id: 'ubp.calculator.needs.automations.exploring.list.1',
					defaultMessage: '1–5 flows created',
				}),
			],
			valueProp: defineMessage({
				id: 'ubp.calculator.needs.automations.exploring.valueProp',
				defaultMessage:
					'Best for automating individual tasks and simple team alerts. Focuses on reducing "busy work" like status updates and basic notifications.',
			}),
		},
		adopting: {
			label: defineMessage({
				id: 'ubp.calculator.needs.automations.adopting.label',
				defaultMessage: 'Adopting',
			}),
			description: defineMessage({
				id: 'ubp.calculator.needs.automations.adopting.description',
				defaultMessage:
					'Best for automating team collaboration across your Atlassian system of work.',
			}),
			// usageRange omitted — Automations bullets already convey the range.
			list: [
				defineMessage({
					id: 'ubp.calculator.needs.automations.adopting.list.0',
					defaultMessage: '11–200 steps run per seat per month',
				}),
				defineMessage({
					id: 'ubp.calculator.needs.automations.adopting.list.1',
					defaultMessage: '6–50 flows created',
				}),
			],
			valueProp: defineMessage({
				id: 'ubp.calculator.needs.automations.adopting.valueProp',
				defaultMessage:
					'Designed for teams scaling automation across multiple projects. Includes multi-step flows, cross-product flows, and standardising team rituals.',
			}),
		},
		scaling: {
			label: defineMessage({
				id: 'ubp.calculator.needs.automations.scaling.label',
				defaultMessage: 'Scaling',
			}),
			description: defineMessage({
				id: 'ubp.calculator.needs.automations.scaling.description',
				defaultMessage:
					'Best for driving organizational efficiency and democratizing automation for all teams.',
			}),
			// usageRange omitted — Automations bullets already convey the range.
			list: [
				defineMessage({
					id: 'ubp.calculator.needs.automations.scaling.list.0',
					defaultMessage: '200+ steps run per seat per month',
				}),
				defineMessage({
					id: 'ubp.calculator.needs.automations.scaling.list.1',
					defaultMessage: '60+ flows created',
				}),
			],
			valueProp: defineMessage({
				id: 'ubp.calculator.needs.automations.scaling.valueProp',
				defaultMessage:
					'For organizations running complex business processes at scale. Supports advanced orchestration, global rules, and high-frequency syncs across the entire enterprise.',
			}),
		},
	},
	valueProps: [
		{
			title: defineMessage({
				id: 'ubp.calculator.estimate.automations.valueProps.0.title',
				defaultMessage: 'Automate everyday tasks',
			}),
			description: defineMessage({
				id: 'ubp.calculator.estimate.automations.valueProps.0.description',
				defaultMessage:
					'Use automation steps to handle repetitive work like updating fields, sending notifications, creating recurring pages, and keeping issues moving through your workflow.',
			}),
		},
		{
			title: defineMessage({
				id: 'ubp.calculator.estimate.automations.valueProps.1.title',
				defaultMessage: 'Connect workflows across tools',
			}),
			description: defineMessage({
				id: 'ubp.calculator.estimate.automations.valueProps.1.description',
				defaultMessage:
					'Build multi-step rules that sync work across Jira, Confluence, Loom, and third-party tools — so teams stay aligned without manual follow-up.',
			}),
		},
		{
			title: defineMessage({
				id: 'ubp.calculator.estimate.automations.valueProps.2.title',
				defaultMessage: 'Orchestrate business processes at scale',
			}),
			description: defineMessage({
				id: 'ubp.calculator.estimate.automations.valueProps.2.description',
				defaultMessage:
					'Support complex, high-volume processes with branching logic, global rules, and AI-powered steps that help teams summarize, route, and respond to work faster.',
			}),
		},
	],
};
